import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

interface LineItem {
  productId?: string;
  description: string;
  quantityKg: string;
  pricePerKg: string;
}

const emptyLineItem: LineItem = { description: '', quantityKg: '', pricePerKg: '' };

export const InvoiceForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  const duplicateData = (location.state as any)?.duplicate;

  const [form, setForm] = useState({
    buyerName: '', buyerEmail: '', buyerCompany: '', buyerAddress: '',
    contractReference: '',
    currency: 'USD', paymentTerms: 'Net 30', taxRate: '0', dueDate: '', notes: '',
    senderEntity: 'lot20',
  });
  const [lineItems, setLineItems] = useState<LineItem[]>([{ ...emptyLineItem }]);
  const [coffees, setCoffees] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/coffees').then(r => r.json()).then(data => {
      setCoffees(Array.isArray(data) ? data : []);
    }).catch(() => {});

    if (isEdit) {
      fetch(`/api/invoices/${id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setForm({
            buyerName: data.buyerName || '',
            buyerEmail: data.buyerEmail || '',
            buyerCompany: data.buyerCompany || '',
            buyerAddress: data.buyerAddress || '',
            contractReference: data.contractReference || '',
            currency: data.currency || 'USD',
            paymentTerms: data.paymentTerms || 'Net 30',
            taxRate: data.taxRate || '0',
            dueDate: data.dueDate || '',
            notes: data.notes || '',
            senderEntity: data.senderEntity || 'lot20',
          });
          if (data.lineItems?.length) {
            setLineItems(data.lineItems.map((li: any) => ({
              productId: li.productId || undefined,
              description: li.description,
              quantityKg: li.quantityKg,
              pricePerKg: li.pricePerKg,
            })));
          }
        });
    } else if (duplicateData) {
      setForm(prev => ({ ...prev, ...duplicateData, dueDate: '' }));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const updateLineItem = (i: number, field: keyof LineItem, value: string) => {
    setLineItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  const selectCoffeeForLine = (i: number, coffeeId: string) => {
    const coffee = coffees.find(c => c.id === coffeeId);
    if (!coffee) return;
    setLineItems(prev => {
      const next = [...prev];
      next[i] = {
        productId: coffee.id,
        description: coffee.title,
        quantityKg: next[i].quantityKg || '',
        pricePerKg: coffee.pricePerKg || coffee.price || '',
      };
      return next;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...emptyLineItem }]);
  const removeLine = (i: number) => setLineItems(prev => prev.filter((_, j) => j !== i));

  const subtotal = lineItems.reduce((sum, li) => {
    return sum + (parseFloat(li.quantityKg) || 0) * (parseFloat(li.pricePerKg) || 0);
  }, 0);
  const taxAmount = subtotal * ((parseFloat(form.taxRate) || 0) / 100);
  const total = subtotal + taxAmount;

  const fmtCurrency = (amount: number) => {
    const symbols: Record<string, string> = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KES ' };
    const sym = symbols[form.currency] || form.currency + ' ';
    return `${sym}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const validItems = lineItems.filter(li => li.description && li.quantityKg && li.pricePerKg);
    if (validItems.length === 0) {
      setError('Add at least one line item with description, quantity, and price.');
      setSaving(false);
      return;
    }

    const body = isEdit
      ? { id, ...form, lineItems: validItems }
      : { ...form, lineItems: validItems };

    try {
      const res = await fetch('/api/admin/invoices', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }

      navigate('/admin/invoices');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Invoice' : 'New Invoice'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">
            {isEdit ? 'Update invoice details' : duplicateData ? 'Duplicated from previous invoice' : 'Create a new invoice'}
          </p>
        </div>
        <button onClick={() => navigate('/admin/invoices')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
          Back to invoices
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sender Entity */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Invoicing Entity</h2>
          <div className="grid grid-cols-2 gap-4">
            <label className={`flex items-center gap-3.5 p-4 border rounded-xl cursor-pointer transition-all ${form.senderEntity === 'lot20' ? 'border-[#1A1A1A]/30 bg-[#FAFAF8] shadow-sm' : 'border-[#E8E6E2] hover:border-[#A3A3A3]'}`}>
              <input type="radio" name="senderEntity" value="lot20" checked={form.senderEntity === 'lot20'} onChange={handleChange} className="accent-[#1A1A1A]" />
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">Lot Twenty Coffee Farmers Services</p>
                <p className="text-xs text-[#A3A3A3]">Sosiot Road, Kericho</p>
              </div>
            </label>
            <label className={`flex items-center gap-3.5 p-4 border rounded-xl cursor-pointer transition-all ${form.senderEntity === 'alpine' ? 'border-[#1A1A1A]/30 bg-[#FAFAF8] shadow-sm' : 'border-[#E8E6E2] hover:border-[#A3A3A3]'}`}>
              <input type="radio" name="senderEntity" value="alpine" checked={form.senderEntity === 'alpine'} onChange={handleChange} className="accent-[#1A1A1A]" />
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">Alpine Coffee Kenya Ltd Co.</p>
                <p className="text-xs text-[#A3A3A3]">Kanyingombe, Rongo, Migori</p>
              </div>
            </label>
          </div>
        </div>

        {/* Client details */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Client Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Company Name</label>
              <input name="buyerCompany" value={form.buyerCompany} onChange={handleChange} placeholder="East Africa Coffee Co." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Contact Name</label>
              <input name="buyerName" value={form.buyerName} onChange={handleChange} required placeholder="John Smith" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input name="buyerEmail" type="email" value={form.buyerEmail} onChange={handleChange} placeholder="accounts@company.com" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Address</label>
              <input name="buyerAddress" value={form.buyerAddress} onChange={handleChange} placeholder="123 Street, City, Country" className={inputClass} />
            </div>
          </div>
        </div>

        {/* Terms & reference */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Terms & Reference</h2>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className={labelClass}>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange} className={inputClass}>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (&euro;)</option>
                <option value="GBP">GBP (&pound;)</option>
                <option value="KES">KES</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Payment Terms</label>
              <select name="paymentTerms" value={form.paymentTerms} onChange={handleChange} className={inputClass}>
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="Payment Before Release">Before Release</option>
                <option value="Net 7">Net 7</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 60">Net 60</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Tax Rate (%)</label>
              <input name="taxRate" type="number" step="0.01" value={form.taxRate} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Due Date</label>
              <input name="dueDate" type="date" value={form.dueDate} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Contract Ref.</label>
              <input name="contractReference" value={form.contractReference} onChange={handleChange} placeholder="e.g. 4124267720" className={inputClass} />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Line Items</h2>

          <div className="flex gap-3 items-end mb-2 px-1">
            <div className="flex-1"><span className={labelClass}>Coffee / Description</span></div>
            <div className="w-28"><span className={labelClass}>Qty (kg)</span></div>
            <div className="w-28"><span className={labelClass}>Price / kg</span></div>
            <div className="w-28 text-right"><span className={labelClass}>Line Total</span></div>
            <div className="w-8" />
          </div>

          <div className="space-y-2">
            {lineItems.map((li, i) => {
              const lineTotal = (parseFloat(li.quantityKg) || 0) * (parseFloat(li.pricePerKg) || 0);
              return (
                <div key={i} className="flex gap-3 items-center p-3 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/40">
                  <div className="flex-1">
                    <div className="flex gap-2">
                      <select
                        value={li.productId || ''}
                        onChange={e => selectCoffeeForLine(i, e.target.value)}
                        className="w-36 px-2.5 py-2 border border-[#E8E6E2] rounded-lg bg-white text-sm text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10"
                      >
                        <option value="">Custom item</option>
                        {coffees.map(c => (
                          <option key={c.id} value={c.id}>{c.title}</option>
                        ))}
                      </select>
                      <input
                        value={li.description}
                        onChange={e => updateLineItem(i, 'description', e.target.value)}
                        placeholder="Item description"
                        className={`${inputClass} flex-1`}
                      />
                    </div>
                  </div>
                  <div className="w-28">
                    <input type="number" step="0.01" value={li.quantityKg} onChange={e => updateLineItem(i, 'quantityKg', e.target.value)} placeholder="0.00" className={inputClass} />
                  </div>
                  <div className="w-28">
                    <input type="number" step="0.01" value={li.pricePerKg} onChange={e => updateLineItem(i, 'pricePerKg', e.target.value)} placeholder="0.00" className={inputClass} />
                  </div>
                  <div className="w-28 text-right">
                    <p className="text-sm font-semibold text-[#1A1A1A] tabular-nums">{fmtCurrency(lineTotal)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    className="w-8 h-8 flex items-center justify-center text-[#C4C4C4] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    disabled={lineItems.length === 1}
                  >
                    &times;
                  </button>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addLine}
            className="mt-4 px-4 py-2.5 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] border border-dashed border-[#E8E6E2] hover:border-[#A3A3A3] rounded-xl transition-all w-full"
          >
            + Add line item
          </button>

          <div className="mt-6 pt-5 border-t border-[#E8E6E2]/60 flex justify-end">
            <div className="w-72 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#A3A3A3]">Subtotal</span>
                <span className="text-[#1A1A1A] tabular-nums font-medium">{fmtCurrency(subtotal)}</span>
              </div>
              {parseFloat(form.taxRate) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#A3A3A3]">Tax ({form.taxRate}%)</span>
                  <span className="text-[#1A1A1A] tabular-nums font-medium">{fmtCurrency(taxAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-3 border-t-2 border-[#1A1A1A]">
                <span className="text-[#1A1A1A]">Total</span>
                <span className="text-[#1A1A1A] tabular-nums">{fmtCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Contract references, shipping terms, additional instructions..." className={inputClass} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10">
            {saving ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}
          </button>
          <button type="button" onClick={() => navigate('/admin/invoices')} className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
